// file: pages/Admin/UserManagement.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import {
  Flex, Box, Text, Badge,
} from "@chakra-ui/react";
import {
  EllipsisVertical, Plus, Pencil, Contact, Archive, ShieldAlert, MessageSquare, FileText
} from "lucide-react";
import GradientButton from "components/Buttons/GradientButton";
import AddUserDrawer from "./components/AddUserDrawer";
import ViewProfile from "./components/ViewProfile";
import EditUserDrawer from "./components/EditUserDrawer";
import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";
import ArchiveUser from "./components/ArchiveUser";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsersAction, registerUserAction, updateUserAction } from '../../../state/actions/userActions';
import { deleteAccount, confirmAdminPassword } from "state/actions/authActions";
import { formatDate } from "utils/dateFormatter";
import { useCustomNotification } from 'components/Toasts/OpenNotification.js';
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import ManageDeletionModal from "./components/ManageDeletionModal";
import AddContactLogDrawer from "../ContactLogManagement/components/AddContactLogDrawer";
import {
  fetchUserContactLogsAction,
  createContactLogAction,
} from "../../../state/actions/contactLogActions";
import ViewContactLogsDrawer from "../ContactLogManagement/components/ViewContactLogsDrawer";

const UserManagement = () => {
  const [isAddUserDrawerOpen, setAddUserDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewProfileDrawerOpen, setViewProfileDrawerOpen] = useState(false);
  const [isEditUserDrawerOpen, setEditUserDrawerOpen] = useState(false);
  const [isArchiveUserDialogOpen, setArchiveUserDialogOpen] = useState(false);
  const [selectedUserForArchive, setSelectedUserForArchive] = useState(null);
  const [isManageDeletionOpen, setManageDeletionOpen] = useState(false);
  const [selectedUserForDeletion, setSelectedUserForDeletion] = useState(null);
  const [isAddContactLogOpen, setAddContactLogOpen] = useState(false);
  const [selectedUserForLog, setSelectedUserForLog] = useState(null);
  const [isContactLogsOpen, setContactLogsOpen] = useState(false);
  const [contactLogsUser, setContactLogsUser] =useState(null);
  const history = useHistory();
  const showToast = useCustomNotification();
  const dispatch = useDispatch();


  const { users: reduxUsers, loading, error, page, hasMore, total, counts } =
    useSelector((state) => state.users);
  const users = reduxUsers || [];
  const {
  logs: contactLogs,
  loading: contactLogsLoading,
} = useSelector(
  (state) => state.contactLogs
);
  const adminCount = counts?.["usertype.admin"] ?? 0;
  const individualCount = counts?.["usertype.individual"] ?? 0;
  const driverCount = counts?.["usertype.driver"] ?? 0;
  const businessCount = counts?.["usertype.business"] ?? 0;
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;
  const [userFilter, setUserFilter] = useState("all");
  const [selectedType, setSelectedType] = useState("All");
  const [searchField, setSearchField] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const handleSearch = (field, value) => {
    setCurrentPage(1);
    setSearchField(field);
    setSearchTerm(value);
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);
  useEffect(() => {
    console.log("selectedType", selectedType);
  }, [selectedType]);
  const displayLabel =
    selectedType === "Individual"
      ? "All Individuals"
      : selectedType === "Driver"
        ? "All Drivers"
        : selectedType === "Business"
          ? "All Businesses"
          : selectedType === "Admin"
            ? "All Admins"
            : selectedType === "Agent"
              ? "All Agents"
              : "All Users";

  const displayTotal =
    selectedType === "Individual"
      ? individualCount
      : selectedType === "Driver"
        ? driverCount
        : selectedType === "Business"
          ? businessCount
          : selectedType === "Admin"
            ? adminCount
            : total;

  const loadUsers = useCallback(async () => {
    try {
      await dispatch(
        fetchUsersAction(
          currentPage,
          PAGE_SIZE,
          searchField,
          searchValue
        )
      );
    } catch (error) {
      console.error("loadUsers dispatch error:", error);
    }
  }, [
    dispatch,
    currentPage,
    searchField,
    searchValue,
  ]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAddUser = async (newUser) => {
    const response = await dispatch(registerUserAction(newUser));

    if (response.success) {
      showToast({ type: "success", message: "Admin added successfully" });
      handleCloseAddUserDrawer();
    } else {
      showToast({
        type: "error",
        message: "Registration Failed",
        description: response.error.split('\n').map(msg => ` ${msg}`).join('\n'),
      });
    }
  };

  const handleUpdateUser = async (updatedUser) => {
    try {
      await dispatch(updateUserAction(updatedUser.user_id, updatedUser));

      showToast({
        type: "success",
        message: "User Updated",
        description: "User details updated successfully.",
      });

      setEditUserDrawerOpen(false);

    } catch (error) {
      showToast({
        type: "error",
        message: "Update Failed",
        description: error.message || "Failed to update user",
      });
    }
  };

  const handleDeleteAccount = async (userId) => {
    try {
      const response = await dispatch(deleteAccount(userId));

      if (response.success) {
        showToast({ type: "success", message: "Account deleted successfully." });
        setManageDeletionOpen(false);
        setSelectedUserForDeletion(null);

         await dispatch(
        fetchUsersAction(
          currentPage,
          PAGE_SIZE,
          searchField,
          searchValue
        )
      );
      } else {
        showToast({
          type: "error",
          message: "Failed to delete account.",
          description: response.error || "Unknown error",
        });
      }
    } catch (error) {
      showToast({
        type: "error",
        message: "Error deleting account",
        description: error.message || error.toString(),
      });
    }
  };

  const handleConfirmPassword = async (password) => {
    try {
      const response = await dispatch(confirmAdminPassword(password));

      if (response.success) {
        showToast({ type: "success", message: "Password confirmed successfully." });
        setManageDeletionOpen(false);
        setSelectedUserForDeletion(null);
      } else {
        showToast({
          type: "error",
          message: "Password confirmation failed.",
          description: response.error || "Unknown error",
        });
      }

      return response;
    } catch (error) {
      showToast({
        type: "error",
        message: "Error confirming password",
        description: error.message || error.toString(),
      });

      return { success: false, error: error.message || "Unknown error" };
    }
  };

  const handleAddContactLog = async (data) => {
    const res = await dispatch(createContactLogAction(data));

    if (res?.success) {
      showToast({
        type: "success",
        message: "Contact log created successfully",
      });
    } else {
      showToast({
        type: "error",
        message: res?.error || "Failed to create contact log",
      });
    }
  };


  const handleOpenArchiveUserDialog = (user) => {
    setSelectedUserForArchive(user);
    setArchiveUserDialogOpen(true);
  };

  const handleCloseArchiveUserDialog = () => {
    setSelectedUserForArchive(null);
    setArchiveUserDialogOpen(false);
  };

  const handleArchiveUser = () => {
    console.log("Archiving user:", selectedUserForArchive);
  };

  const handleOpenAddUserDrawer = () => setAddUserDrawerOpen(true);
  const handleCloseAddUserDrawer = () => setAddUserDrawerOpen(false);
  const handleViewProfile = (user) => {
    setSelectedUser(user);
    setViewProfileDrawerOpen(true);
  };
  const handleCloseViewProfileDrawer = () => {
    setSelectedUser(null);
    setViewProfileDrawerOpen(false);
  };
  const handleOpenEditUserDrawer = (user) => {
    setSelectedUser(user);
    setEditUserDrawerOpen(true);
  };

  const handleCloseEditUserDrawer = () => {
    setSelectedUser(null);
    setEditUserDrawerOpen(false);
  };
  const openAddLog = useCallback((user) => {
    setSelectedUserForLog(user);
    setAddContactLogOpen(true);
  }, []);
  const openContactLogs = useCallback(
  async (user) => {
    if (!user?.user_id) {
      showToast({
        type: "error",
        message: "Unable to load contact logs",
        description: "User ID is missing.",
      });

      return;
    }

    setContactLogsUser(user);
    setContactLogsOpen(true);

    await dispatch(
      fetchUserContactLogsAction(
        user.user_id
      )
    );
  },
  [dispatch, showToast]
);
  const handleManageDeletionRequest = (user) => {
    setSelectedUserForDeletion(user);
    setManageDeletionOpen(true);
  };

  const handleCloseManageDeletion = () => {
    setSelectedUserForDeletion(null);
    setManageDeletionOpen(false);
  };



  const statusColorScheme = {
    Active: "green",
    Inactive: "yellow",
    Suspended: "red",
  };

  const columnsData = [
    {
      Header: "First Name",
      accessor: "first_name",
    },
    {
      Header: "Last Name",
      accessor: "last_name",
    },
    { Header: "Phone Number", accessor: "phone_number" },
    { Header: "Type", accessor: "user_type" },
   {
  Header: "Status",
  accessor: "status",
  filter: (rows, id, filterValue) =>
    rows.filter(
      (row) =>
        String(row.values[id]).toLowerCase() ===
        String(filterValue).toLowerCase()
    ),
  Cell: ({ value }) => (
    <StatusBadge
      label={value}
      variant={getStatusVariant(value)}
    />
  ),
},

{
  Header: "Deletion Request",
  accessor: (row) =>
    row.deletion_requested
      ? `Requested on ${formatDate(
          row.deletion_requested_at
        )}`
      : "None",

  Cell: ({ row }) => (
    <StatusBadge
      label={
        row.original.deletion_requested
          ? "Pending"
          : "None"
      }
      variant={
        row.original.deletion_requested
          ? "danger"
          : "success"
      }
    />
  ),
},
    {
      Header: "Date Added",
      accessor: "created_at",
      Cell: ({ value }) => formatDate(value),
    },
    {
      Header: "Actions",
      accessor: "moreOptions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={[
            {
              icon: <Contact size={16} />,
              text: "View Profile",
              onClick: () => handleViewProfile(row.original),
            },
            {
              icon: <Pencil size={16} />,
              text: "Edit Details",
              onClick: () => handleOpenEditUserDrawer(row.original),
            },
            {
              icon: <MessageSquare size={16} />,
              text: "Chat with User",
              onClick: () =>
                history.push(`/admin/communications?userId=${row.original.user_id}`),
            },
            {
  icon: <FileText size={16} />,
  text: "Add Contact Log",
  onClick: () =>
    openAddLog(row.original),
},
{
  icon: <Contact size={16} />,
  text: "View Contact Logs",
  onClick: () =>
    openContactLogs(
      row.original
    ),
},
            ...(row.original.deletion_requested
              ? [
                {
                  icon: <ShieldAlert size={16} color="red" />,
                  text: "Manage Deletion Request",
                  onClick: () =>
                    handleManageDeletionRequest(row.original),
                },
              ]
              : []),
          ]}
        />
      )
    },
  ];

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Box p={6}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="md" fontWeight="bold">
            {loading ? (
              <Skeleton height="20px" width="150px" />
            ) : (
              `${displayLabel}: ${displayTotal}`
            )}
          </Text>

          {loading ? (
            <Skeleton height="40px" width="120px" />
          ) : (
            <GradientButton
              icon={<Plus size={16} />}
              onClick={handleOpenAddUserDrawer}
              height="40px"
            >
              Add User
            </GradientButton>
          )}
        </Flex>

      </Box>

      <Box height="70vh" overflowY="auto">
        <ReusableTable
          columnsData={columnsData}
          tableData={users}
          isLoading={loading}
          error={error}
          onTypeFilterChange={setSelectedType}
          onSearch={handleSearch}
          emptyMessage="No users found. Try adjusting your search or filters."
        />
        <Box mt={4} display="flex" justifyContent="center" gap={2}>
          <GradientButton
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            height="30px"
          >
            Previous
          </GradientButton>

          <Text alignSelf="center">Page {currentPage}</Text>

          <GradientButton
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={!hasMore}
            height="30px"
          >
            Next
          </GradientButton>
        </Box>

      </Box>

      <ReusableDrawer
        isOpen={isViewProfileDrawerOpen}
        onClose={handleCloseViewProfileDrawer}
        headerContent={<h2 style={{ fontSize: "md" }}>View Profile</h2>}
        bodyContent={
          selectedUser && (
            <ViewProfile
              user={selectedUser}
              onClose={handleCloseViewProfileDrawer}
            />
          )
        }
      />

      <EditUserDrawer
        isOpen={isEditUserDrawerOpen}
        onClose={handleCloseEditUserDrawer}
        user={selectedUser}
        onUpdateUser={handleUpdateUser}
      />

      <AddUserDrawer
        isOpen={isAddUserDrawerOpen}
        onClose={handleCloseAddUserDrawer}
        onAddUser={handleAddUser}
      />

      <ArchiveUser
        isOpen={isArchiveUserDialogOpen}
        onClose={handleCloseArchiveUserDialog}
        onConfirm={handleArchiveUser}
        firstName={selectedUserForArchive?.first_name || ""}
        lastName={selectedUserForArchive?.last_name || ""}
      />
      <ManageDeletionModal
        isOpen={isManageDeletionOpen}
        onClose={handleCloseManageDeletion}
        onConfirm={() => handleDeleteAccount(selectedUserForDeletion.user_id)}
        onConfirmPassword={handleConfirmPassword}
        firstName={selectedUserForDeletion?.first_name || ""}
        lastName={selectedUserForDeletion?.last_name || ""}
      />
      <AddContactLogDrawer
        isOpen={isAddContactLogOpen}
        onClose={() => setAddContactLogOpen(false)}
        onAdd={handleAddContactLog}
        user={selectedUserForLog}
      />
      <ViewContactLogsDrawer
  isOpen={isContactLogsOpen}
  onClose={() =>
    setContactLogsOpen(false)
  }
  logs={contactLogs}
  user={contactLogsUser}
  loading={contactLogsLoading}
/>

    </Flex>
  );
};

export default UserManagement;
